import axios from 'axios';
import i18n from '@/plugins/i18n';

import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';

import WorkScheduleUtil from '@/util/work-schedule-util';
import DateTimeUtil from '../util/date-time-util';
import constants from '../constants';

const actions = {

  // General
  loginUser: ({ dispatch, commit, state }, credentials) => axios.post('api/employees/login', credentials)
  .then((response) => {
    commit('login', response.data);
    dispatch('updateTimer', { date: state.timer.trueClockInTime, timeZoneOffset: state.timer.timeZoneOffset });
  })
  .catch((error) => {
    console.log(error);
    throw error;
  }),

  fetchUserProfile: ({ dispatch, commit, state }, authToken) => axios.get(`api/employees/${authToken.sub}/profile`)
  .then((response) => {
    commit('login', response.data);
    dispatch('updateTimer', { date: state.timer.trueClockInTime, timeZoneOffset: state.timer.timeZoneOffset });
  })
  .catch((error) => console.log(error)),

  // eslint-disable-next-line no-empty-pattern
  fetchUserActiveInfo: ({}, authToken) => axios.get(
    `api/employees/${authToken.sub}/active-user-info?lang=${i18n.locale}`,
  )
  .catch((error) => console.error(error)),

  // eslint-disable-next-line no-empty-pattern
  loadSubordinateEmployeesActiveShift: ({}, authToken) => axios.get(
    `api/employees/${authToken.sub}/subordinates/active-shifts`,
  )
  .catch((error) => console.error(error)),

  updateUserAssignments: ({ state, commit }) => axios.get(
    `api/employees/${state.employee.id}/assignments?lang=${i18n.locale}`,
  ).then((response) => {
    commit('setEmployee', response.data);
  })
  .catch((error) => console.log(error)),

  fetchLicense: ({ commit }) => axios.get('api/info/license')
  .then((response) => commit('setLicense', response.data))
  .catch((error) => console.log(error)),

  fetchVersion: ({ commit }) => axios.get('api/info/version')
  .then((response) => commit('setVersion', response.data))
  .catch((error) => console.log(error)),

  saveUserConfiguration: ({ state, commit }, userConfiguration) => axios.put(
    `api/employees/${state.employee.id}/configuration`,
    userConfiguration,
  )
  .then((response) => commit('setUserConfiguration', response.data))
  .catch((error) => console.log(error)),

  fetchNotifications: ({ commit, state }) => {
    const dateFormat = DateTimeUtil.getDateTimeFormatApplied(state.userConfiguration);

    return axios.get(`api/employees/${state.employee.id}/notifications?dateTimeFormat=${dateFormat}
    &lang=${i18n.locale}`)
    .then((response) => commit('setNotifications', response.data))
    .catch((error) => console.log(error));
  },

  postNotificationDismissed: ({ commit }, notificationId) => axios.put(`api/notifications/${notificationId}`)
  .then(() => commit('removeNotification', notificationId))
  .catch((error) => console.log(error)),

  postAllNotificationsDismissed: ({ commit, state }) => {
    const notificationIds = state.notifications.map((notification) => notification.notificationId);

    return axios.post('api/notifications', notificationIds)
    .then(() => commit('removeAllNotifications'))
    .catch((error) => console.log(error));
  },

  // eslint-disable-next-line no-empty-pattern
  fetchCustomFieldObjects: ({}, params) => axios.get(
    `api/time-pairs/custom-fields/${params.id}?searchString=${params.searchString}`,
  ),

  // eslint-disable-next-line consistent-return
  loadCorrectionReasons: ({ commit, state }) => {
    if (isEmpty(state.correctionReasons)) {
      return axios.get(`api/info/reasons?lang=${i18n.locale}`)
      .then((response) => commit('setCorrectionReasons', response.data))
      .catch((error) => console.log(error));
    }
  },

  // eslint-disable-next-line no-empty-pattern
  logToServer: ({}, loggingRequest) => axios.post('api/log', loggingRequest),

  // Employee
  loadEmployeeTimePairs: ({ commit, state }, params) => axios.get(
    'api/time-pairs/shift-summaries?statuses=PENDING,PENDING_DELETE,REJECTED,APPROVED'
    + `&employeeId=${state.employee.id}`
    + `&startDate=${params.startDate}`
    + `&endDate=${params.endDate}`,
  )
  .then((response) => commit('setEmployeeTimePairsModel', response.data))
  .catch((error) => console.log(error)),

  // eslint-disable-next-line consistent-return
  loadSupportedLocales: ({ commit, state }) => {
    if (isEmpty(state.locales)) {
      return axios.get(`api/info/locales?lang=${i18n.locale}`)
      .then((response) => commit('setLocales', response.data))
      .catch((error) => console.log(error));
    }
  },

  saveShiftTimePair: ({ commit }, payload) => axios.post(
    `api/time-pairs/manual-shift-pair?timePairId=${payload.timePair.id}`
    + `&notificationIds=${payload.notificationIds}&lang=${i18n.locale}`, payload.timePair,
  )
  .then((response) => {
    if (payload.isApproval) {
      commit(
        'updateApprovals',
        { timePairId: payload.timePair.id, newTimePair: response.data, employee: payload.employee },
      );
    } else {
      commit(
        'updateEmployeeTimePairs',
        { timePairId: payload.timePair.id, newTimePair: response.data },
      );
      payload.notificationIds.forEach((id) => commit('removeNotification', id));
    }
  }),

  saveSegmentTimePair: ({ commit }, payload) => axios.post(
    `api/time-pairs/manual-segment-pair?timePairId=${payload.timePair.id}&lang=${i18n.locale}`, payload.timePair,
  )
  .then((response) => {
    if (payload.isApproval) {
      commit(
        'updateApprovals',
        { timePairId: payload.timePair.shiftTimePairId, newTimePair: response.data, employee: payload.employee },
      );
    } else {
      commit(
        'updateEmployeeTimePairs',
        { timePairId: payload.timePair.shiftTimePairId, newTimePair: response.data },
      );
    }
  }),

  deleteTimePair: ({ commit }, payload) => axios.delete(`api/time-pairs/${payload.timePair.id}?`
  + `notificationIds=${payload.notificationIds}&lang=${i18n.locale}`)
  .then((response) => commit(
    'removeEmployeeTimePair',
    { shiftTimePairId: payload.shiftTimePair.id, newShiftTimePair: response.data },
  ),
  payload.notificationIds.forEach((id) => commit('removeNotification', id))),

  // eslint-disable-next-line no-empty-pattern
  saveShiftTimeEntry: ({}, timeEntry) => {
    const endPoint = (timeEntry.entryType === constants.EntryType.CLOCK_IN) ? 'shift-clock-in' : 'shift-clock-out';
    return axios.post(`api/time-pairs/${endPoint}?lang=${i18n.locale}`, timeEntry);
  },

  // eslint-disable-next-line no-empty-pattern
  saveSegmentTimeEntry: ({}, timeEntry) => {
    const endPoint = (timeEntry.entryType === constants.EntryType.CLOCK_IN) ? 'segment-clock-in' : 'segment-clock-out';
    return axios.post(`api/time-pairs/${endPoint}?lang=${i18n.locale}`, timeEntry);
  },

  // Manager

  // eslint-disable-next-line no-empty-pattern
  saveShiftTimeEntries: ({}, timeEntries) => {
    const endpoint = (timeEntries[0].entryType === constants.EntryType.CLOCK_IN)
      ? 'mass-shift-clock-in' : 'mass-shift-clock-out';
    return axios.post(`api/time-pairs/${endpoint}?lang=${i18n.locale}`, timeEntries);
  },

  // TODO change statuses for license implementation
  loadManagerApprovals: ({ commit, state }, statuses) => axios.get(
    `api/time-pairs/approvals?statuses=${statuses}&managerId=${state.employee.id}&lang=${i18n.locale}`,
  )
  .then((response) => commit('setManagerApprovals', response.data))
  .catch((error) => console.log(error)),

  loadSubordinateEmployees: ({ commit, state, dispatch }) => axios.get(
    `api/employees/${state.employee.id}/subordinates`,
  )
  .then((response) => {
    commit('setSubordinateEmployees', response.data);
    if (WorkScheduleUtil.getTempWorkScheduleIdsForPendingAssignmentRequests(state).length > 0) {
      dispatch('checkForTempWorkScheduleAssignmentRequestStatuses');
    }
  })
  .catch((error) => console.log(error)),

  loadWorkSchedules: ({ commit }) => axios.get('api/work-schedules')
  .then((response) => commit('setWorkSchedules', response.data))
  .catch((error) => console.log(error)),

  // eslint-disable-next-line no-empty-pattern
  assignTempWorkSchedules: ({ commit, dispatch }, assignRequest) => axios.post(
    `api/work-schedules/temp-assignment?lang=${i18n.locale}`,
    assignRequest,
  )
  .then((response) => commit('updateTempWorkScheduleAssignmentRequestStatuses', response.data))
  .then(() => dispatch('checkForTempWorkScheduleAssignmentRequestStatuses'))
  .catch((error) => {
    console.log(error);
    throw error;
  }),

  // eslint-disable-next-line no-empty-pattern
  assignRotation: ({}, assignRequest) => axios.post(`api/work-schedules/rotation?lang=${i18n.locale}`, assignRequest),

  checkForTempWorkScheduleAssignmentRequestStatuses: ({ state, commit }, timeout, interval) => {
    let tempWorkScheduleIds = WorkScheduleUtil.getTempWorkScheduleIdsForPendingAssignmentRequests(state);
    const endTime = Number(new Date()) + (timeout || 60000);
    interval = interval || 1000;

    const poll = function checkStatuses() {
      axios.get(`api/work-schedules/temp-assignment-statuses?externalCodes=${tempWorkScheduleIds}`)
      .then((response) => {
        commit('updateTempWorkScheduleAssignmentRequestStatuses', response.data);
        tempWorkScheduleIds = WorkScheduleUtil.getTempWorkScheduleIdsForPendingAssignmentRequests(state);

        if (Number(new Date()) < endTime && tempWorkScheduleIds.length > 0) {
          setTimeout(poll, interval);
        } else {
          commit('setTempWorkScheduleAssignmentStatusPolling', false);
          state.pendingWorkSchedules = [];
        }
      })
      .catch(() => {
        commit('setTempWorkScheduleAssignmentStatusPolling', false);
        state.pendingWorkSchedules = [];
      });
    };

    if (tempWorkScheduleIds.length > 0) {
      commit('setTempWorkScheduleAssignmentStatusPolling', true);
    } else {
      commit('setTempWorkScheduleAssignmentStatusPolling', false);
      state.pendingWorkSchedules = [];
    }

    poll();
  },

  loadAssignedWorkScheduleRanges: ({ commit }, request) => axios.get('api/work-schedules/assignment-summaries'
    + `?assignmentIds=${request.assignmentIds}`
    + `&startDate=${request.startDate}`
    + `&endDate=${request.endDate}`)
  .then((response) => commit('setAssignedWorkScheduleRanges', response.data))
  .catch((error) => console.log(error)),

  loadEmployeeSchedules: ({ commit, state }, request) => axios.get(
    'api/work-schedules/employee-assignment-summaries'
    + `?employeeId=${state.employee.id}`
    + `&startDate=${request.startDate}`
    + `&endDate=${request.endDate}`,
  )
  .then((response) => commit('setEmployeeSchedules', response.data))
  .catch((error) => console.log(error)),

  // eslint-disable-next-line no-empty-pattern
  postApprovals: ({}, approvalRequest) => axios.post('api/time-pairs/approvals', approvalRequest),

  // eslint-disable-next-line no-empty-pattern
  loadTimePairCorrectionHistory: ({}, timePairId) => axios.get(`api/time-pairs/${timePairId}/corrections`),

  loadAllWorkSchedules: ({ commit }) => axios.get('api/work-schedules/')
  .then((response) => commit('setWorkSchedules', response.data))
  .catch((error) => console.log(error)),

  updateTimer: ({ commit, dispatch, state }, timeEntry) => {
    commit('setTimerTrueClockIn', timeEntry);
    dispatch('incrementTimer', {
      activeSegmentType: state.activeSegmentType,
    });
  },

  incrementTimer: ({ dispatch, commit, state }, { activeSegmentType }) => {
    if (state.activeTimePair.id != null && activeSegmentType === state.activeSegmentType
      && state.activeTimePair.clockOut == null) {
      const timerValue = DateTimeUtil.getTimerValue(state);
      commit('setTimerDuration', DateTimeUtil.getTimerHmsFormat(timerValue));
      setTimeout(
        () => dispatch('incrementTimer', { activeSegmentType }),
        DateTimeUtil.constants.clockTimerTickAmount * DateTimeUtil.constants.clockTimerTickUnitRefreshRate,
      );
    } else if (state.activeTimePair.id == null) {
      commit('setTimerDuration', '00:00:00');
    }
  },

  selectTimePair: ({ commit, getters }, payload) => {
    payload.approvals = getters.getFilteredTimes(getters.getApprovalsByUsername(payload.username).summaries);
    commit('selectTimePair', payload);
    commit('updateGlobalCheckboxes', payload);
  },

  selectCurrentlySelectablePairsByUsername: ({ commit, getters }, payload) => {
    payload.approvals = getters.getFilteredTimes(getters.getApprovalsByUsername(payload.username).summaries);
    commit('selectCurrentlySelectablePairsByUsername', payload);
    commit('updateGlobalCheckboxes', payload);
  },

  selectAllCurrentlySelectableTimePairs: ({ state, commit, getters }, value) => {
    forEach(keys(state.approvalsByUsername), (username) => {
      const approvals = getters.getFilteredTimes(getters.getApprovalsByUsername(username).summaries);
      commit('selectCurrentlySelectablePairsByUsername', { username, value, approvals });
      commit('updateGlobalCheckboxes', { username, value, approvals });
    });
  },

  clearSelectedTimePairs: ({ commit, dispatch }) => {
    commit('clearSelectedTimePairs');
    dispatch('updateAllGlobalCheckboxes');
  },

  addFilter: ({ commit, dispatch }, payload) => {
    commit('addFilter', payload.filter);
    if (payload.isApproval) {
      dispatch('updateAllGlobalCheckboxes');
    }
  },

  removeFilter: ({ state, commit, getters }, payload) => {
    commit('removeFilter', payload.filter);
    if (payload.isApproval) {
      forEach(keys(state.approvalsByUsername), (username) => {
        const approvals = getters.getFilteredTimes(getters.getApprovalsByUsername(username).summaries);
        commit('updateGlobalCheckboxes', { username, approvals });
      });
    }
  },

  updateAllGlobalCheckboxes: ({ state, commit, getters }) => {
    if (isEmpty(state.approvalsByUsername)) {
      commit('setAllCurrentTimePairsSelected', false);
    } else {
      forEach(keys(state.approvalsByUsername), (username) => {
        const approvals = getters.getFilteredTimes(getters.getApprovalsByUsername(username).summaries);
        commit('updateGlobalCheckboxes', { username, approvals });
      });
    }
  },

  updateCurrentTimePairIds: ({ commit, getters }, payload) => {
    payload.approvals = getters.getFilteredTimes(getters.getApprovalsByUsername(payload.username).summaries);
    commit('updateCurrentTimePairIds', payload);
    commit('updateGlobalCheckboxes', payload);
  },

  fetchFeatureSwitches: async ({ commit }) => {
    await axios.get(`api/info/features?timeZoneOffset=${new Date().getTimezoneOffset() * -1}`).then(
      (response) => {
        commit('setFeatureSwitches', response.data);
      },
    ).catch((error) => {
      console.log(error);
    });
  },

  fetchTheme: async ({ commit }) => {
    await axios.get('api/info/theme').then((response) => {
      commit('setTheme', response.data);
      return response.data;
    }).catch((error) => {
      console.log(error);
    });
  },

  loadDefaultApprovalStatusFilter: ({ commit }) => axios.get(
    'api/info/default-approval-status-filter',
  ).then((response) => commit('setApprovalStatusConfiguration', response.data)),

  loadEmployeeAbsences: ({ commit }, employees) => axios.get(
    `api/employees/absences?employees=${employees}`,
  ).then(
    (response) => commit('setEmployeeAbsences', response.data),
  ).catch((error) => console.log(error)),
};

export default actions;
